/**
 * Homescreen server state (§12.2): three queries — profiles, groups, status — and the mutations
 * that write to them.
 *
 * The profiles list is always fetched with `includeArchived=1` and narrowed client-side, so the
 * table's Archive and Group dropdowns are instant and never refetch — #4 has no group filter and
 * would need a round trip per selection. Every mutation invalidates narrowly; SSE (`run.updated`,
 * `profile.status`, `status.changed`) keeps the same keys fresh while scrapes run.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type QueryKey,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  AddProfilesResponse,
  CreateGroupBody,
  GroupsListResponse,
  NotificationMutesBody,
  NotificationsBlockDTO,
  ProfilesListResponse,
  ScrapeRequestBody,
  ScrapeResponse,
  ScheduleMode,
  StatusResponse,
  SubjectType,
  UpdateGroupBody,
  WatchingBlockDTO,
  WatchTargetBody,
} from '@lw/shared';
import { QK } from '../../api/queryKeys';
import {
  addProfiles,
  archiveProfile,
  listProfiles,
  restoreProfile,
  setProfileGroups,
  updateProfile,
} from '../../api/profiles';
import { createGroup, deleteGroup, listGroups, setGroupMembers, updateGroup } from '../../api/groups';
import { putNotificationTarget } from '../../api/notifications';
import { startScrape } from '../../api/runs';
import { getStatus } from '../../api/status';
import { putWatchTarget } from '../../api/watch';

const PROFILES_QUERY_PARAMS = { includeArchived: true } as const;

export function useProfilesQuery(): UseQueryResult<ProfilesListResponse, Error> {
  return useQuery({
    queryKey: QK.profilesList(PROFILES_QUERY_PARAMS),
    queryFn: ({ signal }) => listProfiles(PROFILES_QUERY_PARAMS, signal),
  });
}

export function useGroupsQuery(): UseQueryResult<GroupsListResponse, Error> {
  return useQuery({
    queryKey: QK.groupsList(),
    queryFn: ({ signal }) => listGroups(signal),
  });
}

export function useStatusQuery(): UseQueryResult<StatusResponse, Error> {
  return useQuery({
    queryKey: QK.status,
    queryFn: ({ signal }) => getStatus(signal),
    // SSE `status.changed` invalidates this; the interval is the belt-and-braces path for a
    // dropped stream (§5.5).
    refetchInterval: 30_000,
  });
}

/** Shared invalidation helper — the homescreen's three keys move together often enough. */
function useHomeInvalidation() {
  const queryClient = useQueryClient();
  return {
    profiles: () => void queryClient.invalidateQueries({ queryKey: QK.profiles }),
    groups: () => void queryClient.invalidateQueries({ queryKey: QK.groups }),
    status: () => void queryClient.invalidateQueries({ queryKey: QK.status }),
    views: () => void queryClient.invalidateQueries({ queryKey: QK.views }),
    watch: () => void queryClient.invalidateQueries({ queryKey: QK.watch }),
  };
}

export function useAddProfiles() {
  const invalidate = useHomeInvalidation();
  return useMutation<AddProfilesResponse, Error, string>({
    mutationFn: (input) => addProfiles({ input }),
    onSuccess: () => {
      invalidate.profiles();
    },
  });
}

export function useSetScheduleMode() {
  const invalidate = useHomeInvalidation();
  return useMutation({
    mutationFn: ({ id, scheduleMode }: { id: number; scheduleMode: ScheduleMode }) =>
      updateProfile(id, { scheduleMode }),
    onSuccess: () => {
      invalidate.profiles();
    },
  });
}

/** #6 with the other half of the body — `''` clears the note server-side. */
export function useSetProfileNotes() {
  const invalidate = useHomeInvalidation();
  return useMutation({
    mutationFn: ({ id, notes }: { id: number; notes: string }) => updateProfile(id, { notes }),
    onSuccess: () => {
      invalidate.profiles();
    },
  });
}

export function useArchiveProfile() {
  const invalidate = useHomeInvalidation();
  return useMutation({
    mutationFn: (id: number) => archiveProfile(id),
    onSuccess: () => {
      invalidate.profiles();
      invalidate.groups();
      invalidate.status();
      invalidate.views();
    },
  });
}

export function useRestoreProfile() {
  const invalidate = useHomeInvalidation();
  return useMutation({
    mutationFn: (id: number) => restoreProfile(id),
    onSuccess: () => {
      invalidate.profiles();
      invalidate.groups();
      invalidate.views();
    },
  });
}

export function useSetProfileGroups() {
  const invalidate = useHomeInvalidation();
  return useMutation({
    mutationFn: ({ id, groupIds }: { id: number; groupIds: number[] }) =>
      setProfileGroups(id, { groupIds }),
    onSuccess: () => {
      invalidate.profiles();
      invalidate.groups();
    },
  });
}

export function useCreateGroup() {
  const invalidate = useHomeInvalidation();
  return useMutation({
    mutationFn: (body: CreateGroupBody) => createGroup(body),
    onSuccess: () => {
      invalidate.groups();
      invalidate.profiles();
    },
  });
}

export function useUpdateGroup() {
  const invalidate = useHomeInvalidation();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: UpdateGroupBody }) => updateGroup(id, body),
    onSuccess: () => {
      invalidate.groups();
      invalidate.profiles();
    },
  });
}

export function useSetGroupMembers() {
  const invalidate = useHomeInvalidation();
  return useMutation({
    mutationFn: ({ id, memberIds }: { id: number; memberIds: number[] }) =>
      setGroupMembers(id, { memberIds }),
    onSuccess: () => {
      invalidate.groups();
      invalidate.profiles();
    },
  });
}

export function useDeleteGroup() {
  const invalidate = useHomeInvalidation();
  return useMutation({
    mutationFn: (id: number) => deleteGroup(id),
    onSuccess: () => {
      invalidate.groups();
      invalidate.profiles();
    },
  });
}

/* ------------------------------------------------- #70 optimistic row patch */

type WatchingRow = { id: number; watching: WatchingBlockDTO };
type WatchingUpdater = (current: WatchingBlockDTO) => WatchingBlockDTO;

/** What `patchWatchingCaches` overwrote, so `onError` can put it back verbatim. */
export type WatchingCacheSnapshot = { key: QueryKey; data: unknown }[];

/** Pure: the same rows with one row's block replaced; `null` when this list has no such row. */
function withWatchingRow<T extends WatchingRow>(
  rows: T[],
  id: number,
  next: WatchingUpdater,
): T[] | null {
  let found = false;
  const patched = rows.map((row) => {
    if (row.id !== id) return row;
    found = true;
    return { ...row, watching: next(row.watching) };
  });
  return found ? patched : null;
}

/**
 * Rewrite one row's `watching` block in **every** cached list of its kind — the homescreen keeps one
 * (`includeArchived=1`), but the patch is written against the key prefix so a second list cannot
 * quietly go stale.
 *
 * A profile toggle moves profile rows only. A group toggle also moves each member's `effectiveOn`
 * and hover sentence, which no client may compute (§12.5) — the invalidation on success is what
 * repairs those, a moment later.
 */
export function patchWatchingCaches(
  queryClient: QueryClient,
  t: SubjectType,
  id: number,
  next: WatchingUpdater,
): WatchingCacheSnapshot {
  const snapshot: WatchingCacheSnapshot = [];
  if (t === 'group') {
    const entries = queryClient.getQueriesData<GroupsListResponse>({ queryKey: QK.groups });
    for (const [key, data] of entries) {
      if (data === undefined) continue;
      const groups = withWatchingRow(data.groups, id, next);
      if (groups === null) continue;
      snapshot.push({ key, data });
      queryClient.setQueryData<GroupsListResponse>(key, { ...data, groups });
    }
    return snapshot;
  }
  const entries = queryClient.getQueriesData<ProfilesListResponse>({ queryKey: QK.profiles });
  for (const [key, data] of entries) {
    if (data === undefined) continue;
    const profiles = withWatchingRow(data.profiles, id, next);
    if (profiles === null) continue;
    snapshot.push({ key, data });
    queryClient.setQueryData<ProfilesListResponse>(key, { ...data, profiles });
  }
  return snapshot;
}

/** Undo of the above: the failed write leaves no trace in the cache. */
export function restoreWatchingCaches(
  queryClient: QueryClient,
  snapshot: WatchingCacheSnapshot,
): void {
  for (const entry of snapshot) queryClient.setQueryData(entry.key, entry.data);
}

/**
 * What a clicked switch shows before #70 answers. `enabled` is the row's own target, and for a group
 * `effectiveOn` *is* that toggle; for a profile it is the union with its groups, so turning one off
 * may bounce back to the quiet "on via group" face when the refetch lands. `hoverDisplay` is left
 * alone — the server writes that sentence, and the response replaces it on success.
 */
export function optimisticWatching(
  current: WatchingBlockDTO,
  enabled: boolean,
): WatchingBlockDTO {
  return { ...current, enabled, effectiveOn: enabled };
}

/**
 * #70 — the Watching toggle in either table, and the group override dialog.
 *
 * Writes immediately, like the Mode cell: there is no draft to batch, and enabling a target starts
 * seeding server-side. The switch moves **optimistically** (SPEC § Frontend): `onMutate` patches the
 * clicked row in the profiles/groups caches and `onError` restores what it took, so a failure both
 * snaps the switch back and lands in the table's `.table-saving` bar. On success the row is repainted
 * from the response's fresh block — server hover sentence included — and both lists plus the settings
 * tab's estimate are re-read, because a group toggle moves every member row too.
 */
export function useSetWatching() {
  const queryClient = useQueryClient();
  const invalidate = useHomeInvalidation();
  return useMutation({
    mutationFn: ({ t, id, body }: { t: SubjectType; id: number; body: WatchTargetBody }) =>
      putWatchTarget(t, id, body),
    onMutate: async ({ t, id, body }) => {
      // The dialog can write cadence/smart alone; only a toggle has a switch to move.
      const enabled = body.enabled;
      if (enabled === undefined) return undefined;
      // A list refetch landing mid-flight would paint the old switch back over the patch.
      await queryClient.cancelQueries({ queryKey: t === 'group' ? QK.groups : QK.profiles });
      return patchWatchingCaches(queryClient, t, id, (current) =>
        optimisticWatching(current, enabled),
      );
    },
    onError: (_error, _variables, snapshot) => {
      if (snapshot !== undefined) restoreWatchingCaches(queryClient, snapshot);
    },
    onSuccess: (response, { t, id }) => {
      patchWatchingCaches(queryClient, t, id, () => response.watching);
      invalidate.profiles();
      invalidate.groups();
      invalidate.watch();
    },
  });
}

/* ------------------------------------------------- #86 optimistic row patch */

type NotificationsRow = { id: number; notifications: NotificationsBlockDTO };

/**
 * The #70 patch, one block over: rewrite one row's `notifications` block in **every** cached list
 * of its kind, and hand back the same `{key, data}[]` snapshot `restoreWatchingCaches` replays
 * (that helper is shape-agnostic, so the rollback is shared rather than duplicated).
 *
 * A group patch moves the group row only. Muting a group also mutes its members, but the row block
 * is each subject's **own** state (§12.5 — the client computes no union), so member rows are
 * unchanged and the success invalidation is what keeps them honest.
 */
export function patchNotificationsCaches(
  queryClient: QueryClient,
  t: SubjectType,
  id: number,
  next: (current: NotificationsBlockDTO) => NotificationsBlockDTO,
): WatchingCacheSnapshot {
  const snapshot: WatchingCacheSnapshot = [];
  const withRow = <T extends NotificationsRow>(rows: T[]): T[] | null => {
    let found = false;
    const patched = rows.map((row) => {
      if (row.id !== id) return row;
      found = true;
      return { ...row, notifications: next(row.notifications) };
    });
    return found ? patched : null;
  };

  if (t === 'group') {
    for (const [key, data] of queryClient.getQueriesData<GroupsListResponse>({
      queryKey: QK.groups,
    })) {
      if (data === undefined) continue;
      const groups = withRow(data.groups);
      if (groups === null) continue;
      snapshot.push({ key, data });
      queryClient.setQueryData<GroupsListResponse>(key, { ...data, groups });
    }
    return snapshot;
  }
  for (const [key, data] of queryClient.getQueriesData<ProfilesListResponse>({
    queryKey: QK.profiles,
  })) {
    if (data === undefined) continue;
    const profiles = withRow(data.profiles);
    if (profiles === null) continue;
    snapshot.push({ key, data });
    queryClient.setQueryData<ProfilesListResponse>(key, { ...data, profiles });
  }
  return snapshot;
}

/** What a clicked checkbox shows before #86 answers: only the keys the request actually sent. */
export function optimisticNotifications(
  current: NotificationsBlockDTO,
  patch: NotificationMutesBody,
): NotificationsBlockDTO {
  return {
    viralPost: patch.viralPost ?? current.viralPost,
    newPost: patch.newPost ?? current.newPost,
    viralPostWatch: patch.viralPostWatch ?? current.viralPostWatch,
  };
}

/**
 * #86 — the kebab's "Notifications" checkboxes on either table.
 *
 * Same shape as `useSetWatching`: writes immediately, moves optimistically, restores the snapshot
 * on failure so the box snaps back and the error lands in the table's `.table-saving` bar, and
 * repaints from the response on success. Both lists are re-read (a group mute changes what its
 * members will fire), but not the watch tab — muting costs nothing and moves no schedule.
 */
export function useSetNotifications() {
  const queryClient = useQueryClient();
  const invalidate = useHomeInvalidation();
  return useMutation({
    mutationFn: ({ t, id, body }: { t: SubjectType; id: number; body: NotificationMutesBody }) =>
      putNotificationTarget(t, id, body),
    onMutate: async ({ t, id, body }) => {
      // A list refetch landing mid-flight would paint the old checkbox back over the patch.
      await queryClient.cancelQueries({ queryKey: t === 'group' ? QK.groups : QK.profiles });
      return patchNotificationsCaches(queryClient, t, id, (current) =>
        optimisticNotifications(current, body),
      );
    },
    onError: (_error, _variables, snapshot) => {
      if (snapshot !== undefined) restoreWatchingCaches(queryClient, snapshot);
    },
    onSuccess: (response, { t, id }) => {
      patchNotificationsCaches(queryClient, t, id, () => response.notifications);
      invalidate.profiles();
      invalidate.groups();
    },
  });
}

export function useStartScrape() {
  const invalidate = useHomeInvalidation();
  return useMutation<ScrapeResponse, Error, ScrapeRequestBody>({
    mutationFn: (body) => startScrape(body),
    onSuccess: () => {
      invalidate.profiles();
      invalidate.status();
    },
  });
}
